<template>
  <b-table
    v-if="entityId"
    :id="`${creativesTableId}_${entityId}`"
    ref="table"
    :category="`PendingApprovals > ${title} > ${entityId}`"
    :items="creativeFiles"
    :columns="creativeFileColumns"
    :fixed-filters="creativeFixedFilters"
    :is-loading="showCreativeLoader"
    :allow-selection="isTableSelectable"
    data-row-height="8rem"
    enable-search-section
    is-editable
    pagination
    @selection-changed="(selectedItems) => emits('selection-changed', selectedItems)"
    @filters-changed="(queryParams) => emits('onCreativesTableFiltersChanged', queryParams)">
    <template #preview="{ item }">
      <creative-preview
        :creative-file="item"
        @thumbnail-clicked="($event) => onCreativeThumbnailClicked($event)"
        @preview-clicked="($event) => onCreativePreviewClicked($event)" />
    </template>
    <template #name="{ item }">
      <router-link
        :to="getLinkToCreative(item)"
        class="entity-link"
        :title="item.name">
        {{ trimFileExtension(item.name) }}
      </router-link>
    </template>
    <template #width="{ item }">
      {{ item.width }}
    </template>
    <template #height="{ item }">
      {{ item.height }}
    </template>
    <template #format="{ item }">
      {{ item.format }}
    </template>
    <template #approval="{ item }">
      <ApprovalStatus :approval="item.prefetchApproval" />
    </template>
    <template
      v-for="(_, slot) in $slots"
      #[slot]="{ item }">
      <slot
        :name="slot"
        v-bind="{ item: item as CreativeFile }" />
    </template>
  </b-table>

  <full-screen-image
    :is-visible="isCreativeThumbnailModalVisible"
    :image-url="activeCreativeThumbnailUrl"
    :name="activeCreativeFileName"
    @close="isCreativeThumbnailModalVisible = false" />

  <full-screen-video
    :is-visible="isCreativePreviewModalVisible"
    :video-url="activeCreativePreviewUrl"
    :name="activeCreativeFileName"
    @close="isCreativePreviewModalVisible = false" />
</template>

<script setup lang="ts">
  import { capitalize, computed, onMounted, ref, useTemplateRef } from 'vue';

  import BTable from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import CreativePreview from '@/common/components/creatives/CreativePreview.vue';
  import { CreativeFile, FilterCollection, QueryParams } from '@/common/models';
  import FullScreenImage from '@/org-app/components/controls/FullScreenImage.vue';
  import FullScreenVideo from '@/org-app/components/controls/FullScreenVideo.vue';
  import ApprovalStatus from '@/org-app/components/pending-approvals/ApprovalStatus.vue';
  import { waitUntil } from '@/utils/DelayHelper';
  import { mapOrgAppGetters, mapOrgAppState } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  interface IProps {
    title: string;
    isExternal: boolean;
    creativesTableId: string;
    entityId: number;
    creativeFixedFilters: FilterCollection[];
    isFromDealDetailsView: boolean;
    showCreativeLoader: boolean;
    allowSelection: boolean;
    creativeFiles: CreativeFile[];
    selectedCreativeFiles?: CreativeFile[];
  }

  const {
    title,
    isExternal,
    creativesTableId,
    entityId,
    creativeFixedFilters,
    isFromDealDetailsView,
    showCreativeLoader,
    allowSelection,
    creativeFiles,
    selectedCreativeFiles = [],
  } = defineProps<IProps>();

  interface IEmits {
    (e: 'onCreativesTableFiltersChanged', queryParams: QueryParams): void;
    (e: 'selection-changed', value: CreativeFile[]): void;
  }

  const emits = defineEmits<IEmits>();

  const { isSupplyOrganization } = mapOrgAppGetters();
  const { organizationSettings } = mapOrgAppState();

  const table = useTemplateRef<InstanceType<typeof BTable>>('table');
  const isCreativeThumbnailModalVisible = ref(false);
  const activeCreativeThumbnailUrl = ref('');
  const activeCreativeFileName = ref('');

  const isCreativePreviewModalVisible = ref(false);
  const activeCreativePreviewUrl = ref('');

  const showActionColumn = computed(() => isExternal && organizationSettings.useInventoryTargetingForCreativeApproval);

  const creativeFileColumns = computed(() => ({
    preview: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.thumbnail')),
      type: TableColumnTypes.image,
    }),
    name: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.name')),
      property: 'name',
      allowSort: true,
    }),
    width: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.width')),
      property: 'width',
      allowSort: false,
    }),
    height: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.height')),
      property: 'height',
      allowSort: false,
    }),
    format: TableColumn.fromObject({
      label: capitalize(i18n.global.t('names.format')),
      property: 'format',
      allowSort: false,
    }),
    ...((!allowSelection || isFromDealDetailsView) &&
      isExternal && {
        approval: TableColumn.fromObject({
          label: capitalize(i18n.global.t('names.approval')),
        }),
      }),
    ...(showActionColumn.value && {
      action: TableColumn.fromObject({
        label: capitalize(i18n.global.t('names.action', { n: 2 })),
        allowSort: false,
        useAsFilter: false,
      }),
    }),
  }));

  const isTableSelectable = computed(() => isSupplyOrganization.value && allowSelection && isExternal);

  onMounted(async () => {
    await waitUntil(() => table.value != null);
    /**
     * Little bit of hack to make sure the selected items set when the component in mounted
     * because the component is destroyed and recreated when the row is collapsed and expanded
     */
    table.value.selectedItems = selectedCreativeFiles;
  });

  function getLinkToCreative(creativeFile: CreativeFile) {
    const creativeID = creativeFile.creativeID;
    return isExternal
      ? `/pendingapprovals/externalcreatives/${creativeID}`
      : `/pendingapprovals/creatives/${creativeID}`;
  }

  function trimFileExtension(fileName) {
    return fileName.replace(/\.[^\\.]+$/, '');
  }

  function onCreativeThumbnailClicked(e) {
    activeCreativeThumbnailUrl.value = e.target.src;
    isCreativeThumbnailModalVisible.value = true;
    const cf = creativeFiles.find((x) => x.imageThumbnailUrl === activeCreativeThumbnailUrl.value);
    if (cf !== undefined) {
      activeCreativeFileName.value = cf.name;
    }
  }

  function onCreativePreviewClicked(e) {
    activeCreativePreviewUrl.value = e.target.src;
    isCreativePreviewModalVisible.value = true;
    const cf = creativeFiles.find((x) => x.videoPreviewUrl === activeCreativePreviewUrl.value);
    if (cf !== undefined) {
      activeCreativeFileName.value = cf.name;
    }
  }
</script>

<style lang="scss" scoped>
  @use '@/styles/variables' as *;

  $border-specs: 1px solid var(--tableHeaderBackgroundColor);
  $border-radius-px: 5px;

  .languages-placeholder {
    color: var(--actionsColor);
  }

  :deep(.table-container) {
    padding: 0.5rem !important;
    background-color: transparent !important;
    box-shadow: none !important;

    .table-scroller {
      overflow-x: visible;
    }
  }

  :deep(table) {
    border-collapse: separate;
    background-color: transparent;

    tr {
      td {
        cursor: unset !important;
        &.first-column {
          left: 0 !important;
        }
        &:first-of-type {
          border-left: $border-specs;
        }

        &:last-of-type {
          border-right: $border-specs;
        }
      }

      &:last-of-type {
        td {
          border-bottom: $border-specs;

          &:first-of-type {
            border-bottom-left-radius: $border-radius-px;
          }

          &:last-of-type {
            border-bottom-right-radius: $border-radius-px;
          }
        }
      }
    }

    th {
      &.first-column {
        left: 0 !important;
      }
      &:first-of-type {
        border-top-left-radius: $border-radius-px;
      }

      &:last-of-type {
        border-top-right-radius: $border-radius-px;
      }
    }
  }
</style>
